import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { CheckCircle2 } from "lucide-react";

import AddEditEvent from "./AddEditEvent";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Tooltip } from "antd";
import { getLeadDetails } from "../../actionCreator/leadDetailsActionCreator";
import { EventStatus } from "../../config";
import { useAlert } from "../../context/AlertProvider";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
dayjs.extend(utc);
dayjs.extend(timezone);

const LeadCalendar = ({
  calendarRef,
  calendarEvents,
  handleEventDrop,
  eventStyles,
  eventIcons,
  events,
  onCreate,
  selectedDate,
}) => {
  const { showAlert } = useAlert();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const eventTypes = useSelector((state) => state.calendarReducer.eventTypes);
  const calendarView = searchParams.get("calendarView");
  const userTz = dayjs.tz.guess();

  const handleEventClick = async (info) => {
    const event = info.event;
    const e = event.extendedProps;
    const res = await dispatch(getLeadDetails(e?.clientLeadId));

    setSelectedEvent({
      ...e,
      leadData: res?.data,
      clientLeadEmail: res?.data?.emailId,
      title: e.title,
      type: e.type,
      dueDate: event.startStr,
      time: e.time,
    });
  };

  return (
    <div
      className={
        ["timeGridWeek", "timeGridDay"]?.includes(calendarView)
          ? "custom-time-calendar"
          : "custom-month-calendar"
      }
    >
      <FullCalendar
        key={`${calendarView}-${selectedDate}`}
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView={calendarView ? calendarView : "dayGridMonth"}
        headerToolbar={false}
        initialDate={
          selectedDate.format("MMMM YYYY") === "Invalid Date"
            ? dayjs().tz(userTz).startOf("day").toISOString()
            : dayjs(selectedDate).tz(userTz).startOf("day").toISOString()
        }
        events={calendarEvents}
        editable
        selectable
        selectMirror
        allDaySlot={false}
        eventStartEditable
        eventDrop={handleEventDrop}
        dayCellDidMount={(info) => {
          if (
            selectedDate &&
            dayjs(selectedDate).format("YYYY-MM-DD") ===
            dayjs(info.date).format("YYYY-MM-DD")
          ) {
            info.el.classList.add("selected-date-cell");
          } else {
            info.el.classList.remove("selected-date-cell");
          }
        }}
        dayHeaderDidMount={(info) => {
          if (
            selectedDate &&
            dayjs(selectedDate).format("YYYY-MM-DD") ===
            dayjs(info.date).format("YYYY-MM-DD")
          ) {
            info.el.classList.add("selected-header-cell");
          } else {
            info.el.classList.remove("selected-header-cell");
          }
        }}
        height="auto"
        eventDisplay="block"
        showNonCurrentDates={false}
        dateClick={(val) => {
          if (dayjs(val.date).isBefore(dayjs(), "day"))
            return showAlert(
              "warning",
              "Events cannot be created for past dates.",
            );

          onCreate(val.dateStr);
        }}
        dayHeaderContent={(arg) => {
          if (
            calendarView === "timeGridWeek" ||
            calendarView === "timeGridDay"
          ) {
            return (
              <div className="tw-text-center">
                <div className="tw-font-semibold">
                  {arg.date.toLocaleDateString("en-US", { weekday: "short" })}
                </div>
                <div
                  className={`${
                    moment(arg.date).isSame(new Date(), "day")
                      ? "tw-text-white tw-bg-theme tw-rounded"
                      : ""
                  } heading16px`}
                >
                  {arg.date.toLocaleDateString("en-US", { day: "numeric" })}
                </div>
              </div>
            );
          }

          return (
            <div className="tw-text-center">
              <div className="tw-font-semibold">
                {arg.date.toLocaleDateString("en-US", { weekday: "short" })}
              </div>
            </div>
          );
        }}
        eventContent={(arg) => {
          const e = {
            ...arg.event.extendedProps,
            type: eventTypes?.find(
              (i) =>
                i?.calendarEventTypeId ===
                arg.event.extendedProps?.calendarEventTypeId,
            )?.typeName,
          };

          return (
            <div
              style={eventStyles[e.type]}
              className="tw-p-2 tw-rounded-md tw-cursor-pointer tw-mb-1 tw-text-[12px] tw-overflow-hidden"
              onClick={() => {
                handleEventClick(arg);
              }}
            >
              <div className="tw-flex tw-items-center">
                {[EventStatus.COMPLETED].includes(e?.status) && (
                  <Tooltip title="Completed event">
                    <CheckCircle2
                      style={{ color: "#52c41a" }}
                      className="tw-mr-1.5 tw-inline-block"
                      size={14}
                    />
                  </Tooltip>
                )}

                <div
                  className={`${
                    [EventStatus.CANCELLED].includes(e?.status)
                      ? "tw-line-through"
                      : ""
                  } tw-text-black tw-font-medium tw-text-text-primary`}
                >
                  <Tooltip title={e.title}>
                    <div className="tw-text-text-primary">
                      {eventIcons[e.type]} {e.title}
                    </div>
                  </Tooltip>
                </div>
              </div>

              <div className="tw-text-slate-500 tw-text-[11px] tw-mt-0.5">
                {dayjs(e?.dueDate).format("hh:mm A")}
              </div>
            </div>
          );
        }}
        dayCellContent={(arg) => (
          <div className="tw-font-semibold">{arg.dayNumberText}</div>
        )}
        eventAllow={(dropInfo, draggedEvent) => {
          const event = events.find(
            (e) => e.calendarEventId === parseInt(draggedEvent.id),
          );
          const type = eventTypes?.find(
            (i) => i?.calendarEventTypeId === event?.calendarEventTypeId,
          )?.typeName;
          const isPast = dayjs(event?.dueDate).isBefore(dayjs(), "day");

          return (
            !isPast &&
            !!type &&
            ![EventStatus.COMPLETED, EventStatus.CANCELLED].includes(
              event?.status,
            )
          );
        }}
      />

      <AddEditEvent
        isOpen={!!selectedEvent}
        handleCancel={() => setSelectedEvent(null)}
        selectedEvent={selectedEvent}
      />
    </div>
  );
};

export default LeadCalendar;
